import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useI18n } from "../i18n-context";

/**
 * Reusable header for inner pages.
 * Renders kicker + headline + optional lead, with a breadcrumb link back to home.
 */
export default function PageHeader({ kicker, title, lead, children, tone = "light" }) {
  const { t, lang } = useI18n();
  const isDark = tone === "dark";

  return (
    <section
      className={`relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden ${
        isDark ? "bg-brand-ink text-white" : "bg-brand-paper text-brand-ink"
      }`}
    >
      <div className={`absolute inset-0 ${isDark ? "bg-grid-dark" : "bg-grid"} opacity-30 pointer-events-none`} />
      <div className="absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-0 h-[400px] w-[400px] rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />

      <div className="container relative">
        <nav className={`flex items-center gap-2 text-xs font-mono uppercase tracking-wider mb-8 ${
          isDark ? "text-white/60" : "text-brand-ink/55"
        }`} data-testid="page-breadcrumb">
          <Link to="/" className="hover:text-brand-orange transition-colors">{t.breadcrumb.home}</Link>
          <ChevronLeft className={`h-3.5 w-3.5 ${lang === "ar" ? "" : "rotate-180"}`} />
          <span className="text-brand-orange">{kicker}</span>
        </nav>

        <div className="kicker !mb-5" data-testid="page-kicker">
          <span className="kicker-dot" />
          {kicker}
        </div>
        <h1
          className={`headline text-4xl sm:text-5xl lg:text-6xl mb-6 max-w-4xl ${
            isDark ? "text-white" : "text-brand-ink"
          }`}
          data-testid="page-title"
        >
          {title}
        </h1>
        {lead && (
          <p
            className={`text-lg md:text-xl max-w-3xl leading-relaxed ${
              isDark ? "text-white/65" : "text-brand-ink/65"
            }`}
            data-testid="page-lead"
          >
            {lead}
          </p>
        )}
        {children && <div className="mt-10">{children}</div>}
      </div>
    </section>
  );
}
